import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Download, Loader2, Sparkles, PenLine } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import UserAvatar from '../UserAvatar';
import { WeekNavigator, currentMonday } from './WeekNavigator';
import { ReportView } from './ReportView';
import { fmtH } from './DayBreakdown';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MemberList = ({ members, selected, onSelect }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-2 lg:sticky lg:top-4" data-testid="report-member-list">
    {members.map((m) => {
      const active = m.id === selected;
      return (
        <button key={m.id} onClick={() => onSelect(m.id)} data-testid={`report-member-${m.id}`}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-50 text-zinc-800'}`}>
          <UserAvatar user={m} size="sm" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold truncate">{m.name}</span>
            <span className={`block text-[11px] ${active ? 'text-white/60' : 'text-zinc-400'}`}>{fmtH(m.total_hours)}</span>
          </span>
          {m.has_report && (m.edited
            ? <PenLine className={`w-4 h-4 shrink-0 ${active ? 'text-white/70' : 'text-[#B45309]'}`} />
            : <Sparkles className={`w-4 h-4 shrink-0 ${active ? 'text-white/70' : 'text-[#047857]'}`} />)}
        </button>
      );
    })}
  </div>
);

export const WeeklyReports = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const [monday, setMonday] = useState(currentMonday());
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(user?.id);
  const [data, setData] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const weekStart = format(monday, 'yyyy-MM-dd');

  const loadOverview = useCallback(async () => {
    if (!isAdmin) return;
    const res = await axios.get(`${API_URL}/reports/weekly/overview`, { params: { week_start: weekStart } });
    setMembers(res.data.members);
  }, [isAdmin, weekStart]);

  const loadReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reports/weekly`, { params: { week_start: weekStart, user_id: selected } });
      setData(res.data);
    } catch (e) {
      toast.error(t('reports.error'));
    }
  }, [weekStart, selected, t]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const reload = async () => { await Promise.all([loadReport(), loadOverview()]); };

  const generateAll = async () => {
    setGeneratingAll(true);
    try {
      const res = await axios.post(`${API_URL}/reports/weekly/generate`, { week_start: weekStart, user_id: 'all' });
      toast.success(t('reports.generatedAllToast', { count: res.data.generated }));
      await reload();
    } catch (e) {
      toast.error(t('reports.error'));
    } finally {
      setGeneratingAll(false);
    }
  };

  return (
    <div data-testid="weekly-reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <WeekNavigator monday={monday} onChange={setMonday} />
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={generateAll} disabled={generatingAll} data-testid="report-generate-all-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full">
              {generatingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{t('reports.generateAllNow')}
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <a href={`${API_URL}/reports/weekly/export?week_start=${weekStart}`} data-testid="report-export-btn">
                <Download className="w-4 h-4 mr-2" />CSV
              </a>
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-zinc-400 mb-5">{t('reports.autoHint')}</p>
      <div className={isAdmin ? 'grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-start' : ''}>
        {isAdmin && <MemberList members={members} selected={selected} onSelect={setSelected} />}
        {data && <ReportView data={data} onReload={reload} />}
      </div>
    </div>
  );
};

export default WeeklyReports;
